import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MapPin, Truck, Package, ShieldCheck, Clock, BarChart3, Globe } from 'lucide-react';
import GradientBackground from '@/components/ui/GradientBackground';
import TextReveal from '@/components/ui/TextReveal';
import { motion } from 'framer-motion';

const OperationCard = ({ icon: Icon, title, description }: { icon: any, title: string, description: string }) => (
  <Card className="hover:shadow-2xl transition-all duration-300 border-foreground/10 bg-foreground/5 backdrop-blur-xl group">
    <CardHeader className="flex flex-row items-center gap-4 pb-2">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
        <Icon size={24} />
      </div>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);

const StatCard = ({ value, label }: { value: string, label: string }) => (
  <div className="text-center p-6 bg-primary/5 rounded-xl border border-primary/10 backdrop-blur-md hover:bg-primary/10 transition-colors">
    <p className="text-3xl sm:text-4xl font-bold text-primary mb-2">{value}</p>
    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
  </div>
);

const AllOperationsPage = () => {
  return (
    <div className="bg-background text-foreground relative min-h-screen overflow-hidden">
      <GradientBackground />

      <div className="container mx-auto py-32 px-4 max-w-7xl relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-24">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-6">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Operational Excellence</span>
          </div>
          <TextReveal className="text-5xl md:text-8xl font-black tracking-tighter text-foreground leading-[0.9] mb-8">
            OUR OPERATIONS.
          </TextReveal>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
          >
            K&C Logistics is transforming Malawi's transport sector with a digital-first approach.
            Here is an overview of our capabilities and nationwide coverage.
          </motion.p>
        </div>

        {/* Core Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
        >
          <OperationCard
            icon={Truck}
            title="Nationwide Transport"
            description="We connect every district in Malawi, from Chitipa to Nsanje. Our network of verified transporters ensures your cargo reaches any destination safely."
          />
          <OperationCard
            icon={Package}
            title="Diverse Cargo Handling"
            description="From agricultural produce and construction materials to general merchandise and specialized equipment. If it fits on a truck, we can move it."
          />
          <OperationCard
            icon={ShieldCheck}
            title="Verified Fleet"
            description="Every vehicle on our platform undergoes a strict verification process, including inspection of insurance, registration, and driver credentials."
          />
          <OperationCard
            icon={MapPin}
            title="Real-Time Tracking"
            description="Our GPS-enabled tracking system provides end-to-end visibility. Clients can monitor their cargo's journey in real-time from pickup to delivery."
          />
          <OperationCard
            icon={Clock}
            title="On-Demand Availability"
            description="Need a truck now? Our platform operates 24/7, allowing you to post jobs and secure transport whenever you need it, reducing downtime."
          />
          <OperationCard
            icon={Globe}
            title="Digital Payments"
            description="We facilitate secure, cashless transactions via PayChangu, ensuring transparency and financial safety for both Clients and Transporters."
          />
        </motion.div>

        {/* Coverage Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="bg-foreground/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 text-center border border-foreground/10">
            <h2 className="text-3xl font-bold mb-6">Operational Coverage</h2>
            <p className="text-muted-foreground mb-8 max-w-3xl mx-auto">
              We are proud to serve all 28 districts of Malawi. Our digital infrastructure allows us to coordinate logistics in urban centers and remote rural areas alike.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm font-medium text-muted-foreground">
              {['Blantyre', 'Lilongwe', 'Mzuzu', 'Zomba', 'Karonga', 'Salima', 'Mangochi', 'Kasungu', 'Dedza', 'Mchinji', 'Ntcheu', 'Balaka'].map((district) => (
                <div key={district} className="bg-background/50 backdrop-blur-sm py-2 px-4 rounded-full border border-foreground/10 shadow-sm hover:bg-primary/10 hover:border-primary/30 transition-colors">
                  {district}
                </div>
              ))}
              <div className="col-span-2 sm:col-span-1 md:col-span-2 lg:col-span-6 py-2 text-primary font-semibold">
                + 16 More Districts
              </div>
            </div>
          </div>
        </motion.div>

        {/* Statistics Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-10">Platform Impact</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <StatCard value="28" label="Districts Covered" />
            <StatCard value="500+" label="Verified Vehicles" />
            <StatCard value="10k+" label="Successful Deliveries" />
            <StatCard value="98%" label="On-Time Rate" />
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center bg-primary text-primary-foreground rounded-3xl p-12 shadow-2xl border border-primary/20"
        >
          <h2 className="text-3xl font-bold mb-4">Ready to Move Your Cargo?</h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Join thousands of businesses and individuals who trust K&C Logistics for their transport needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register?role=CLIENT"
              className="bg-background text-primary hover:bg-background/90 px-8 py-3 rounded-full font-semibold transition-colors shadow-lg"
            >
              Post a Job
            </a>
            <a
              href="/register?role=TRANSPORTER"
              className="bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground border border-primary-foreground/20 px-8 py-3 rounded-full font-semibold transition-colors"
            >
              Join as Transporter
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AllOperationsPage;
